import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopUp } from 'interfaces';
import { getActivePopUps } from 'services';
import PopupModal from './popupModal.index';
import { logPopupView, pickEligiblePopups } from './popupManager.script';

const PopupManager: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [popups, setPopups] = useState<PopUp[]>([]);
    const [current, setCurrent] = useState<PopUp | null>(null);
    const [shownIds, setShownIds] = useState<number[]>([]);
    const pageLoadTimeRef = useRef(Date.now());
    const timeoutRef = useRef<number | undefined>(undefined);

    const clearScheduled = () => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    };

    const showPopup = (popup: PopUp) => {
        setCurrent(popup);
        setShownIds((prev) => (prev.includes(popup.id) ? prev : [...prev, popup.id]));
    };

    const scheduleEligible = (list: PopUp[], path: string, exclude: number[]) => {
        clearScheduled();
        const eligible = pickEligiblePopups(list, path, exclude);
        if (!eligible.length) {
            setCurrent(null);
            return;
        }

        const next = eligible[0];
        const delay = Math.max(0, next.viewAfterSecond * 1000 - (Date.now() - pageLoadTimeRef.current));
        timeoutRef.current = window.setTimeout(() => showPopup(next), delay);
    };

    useEffect(() => {
        let cancelled = false;
        getActivePopUps()
            .then((data) => {
                if (!cancelled) setPopups(data);
            })
            .catch(() => {
                if (!cancelled) setPopups([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setShownIds([]);
        setCurrent(null);
        scheduleEligible(popups, pathname, []);
        return clearScheduled;
    }, [popups, pathname]);

    const handleClose = () => {
        if (current) logPopupView(current.id);
        scheduleEligible(popups, pathname, shownIds);
    };

    const handleLink = (linkTo: string) => {
        if (/^https?:\/\//i.test(linkTo)) {
            window.open(linkTo, '_blank');
            return;
        }
        setCurrent(null);
        clearScheduled();
        navigate(linkTo.startsWith('/') ? linkTo : `/${linkTo}`);
    };

    return (
        <>
            {current && <PopupModal isOpen popup={current} onClose={handleClose} onLink={handleLink} />}
            {children}
        </>
    );
};

export default PopupManager;
